// Pure SEO/AEO scorer extracted from SeoEditorView so it can be reused by the
// copywriter cabinet (and anywhere else) without dragging in the whole project
// context. Same checks/weights as the admin editor; tolerant to word-forms.
//
// computeSeoScore({ html, plainText, keywords, ngrams, recommendedLength })
//   keywords: [{ keyword, frequency }]
//   ngrams:   [{ gram }]
//   recommendedLength: { min, max }
// → { total, checks: [{ name, weight, status: 'ok'|'warn'|'fail', detail }] }

const STOP_KW = new Set(["та","і","в","у","на","з","із","за","до","від","по","для","як","що","це","и","с","по","за","из","от","не","the","a","an","of","in","for","on","to","and","or"]);

function levLE(a, b, max) { // bounded Levenshtein, early exit
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > max) return false;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i]; let best = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      cur[j] = v; if (v < best) best = v;
    }
    if (best > max) return false;
    prev = cur;
  }
  return prev[n] <= max;
}

export function computeSeoScore({ html = "", plainText = "", keywords = [], ngrams = [], recommendedLength = { min: 1500, max: 4000 } }) {
  const text = String(plainText || "").toLowerCase();
  const charCount = text.replace(/\s/g, "").length;
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  if (!text.trim()) return null;

  const pageKeywords = (keywords || [])
    .map(k => typeof k === "string" ? { keyword: k, frequency: 1 } : { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 })
    .filter(k => k.keyword);
  const hfKeyword = pageKeywords[0]?.keyword || "";
  const hfKeys = pageKeywords.filter(k => k.frequency > 1000);
  const mfKeys = pageKeywords.filter(k => k.frequency >= 100 && k.frequency <= 1000);
  const lfKeys = pageKeywords.filter(k => k.frequency >= 10 && k.frequency < 100);
  const pageNgrams = ngrams || [];

  // ── Word-form tolerant keyword matching (укр/рос склонения) ──
  const textWords = Array.from(new Set(text.match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || []));
  const tokMatch = (tok, w) => {
    if (w === tok) return true;
    if (w.length >= tok.length && w.startsWith(tok)) return true;
    if (tok.length >= 5 && w.length >= tok.length - 1 && tok.startsWith(w.slice(0, tok.length - 1))) return true;
    return levLE(tok, w, tok.length <= 4 ? 1 : 2);
  };
  const kwTokens = (phrase) => (String(phrase).toLowerCase().match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || []).filter(t => t.length >= 3 && !STOP_KW.has(t));
  const phraseCovered = (phrase) => {
    if (text.includes(String(phrase).toLowerCase())) return true;
    const toks = kwTokens(phrase);
    if (!toks.length) return false;
    return toks.every(t => textWords.some(w => tokMatch(t, w)));
  };

  const checks = [];
  const hasKeywords = pageKeywords.length > 0;

  // 1. HF keyword (15)
  if (!hasKeywords) {
    checks.push({ name: "ВЧ ключ", weight: 15, status: "warn", detail: "Немає семантики в ТЗ" });
  } else if (hfKeyword) {
    const hfLower = hfKeyword.toLowerCase();
    const hfRegex = new RegExp(hfLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const hfMatches = (text.match(hfRegex) || []).length;
    const inFirst500 = text.substring(0, 500).includes(hfLower);
    const density = wordCount > 0 ? (hfMatches / wordCount) * 100 : 0;
    const ok = hfMatches >= 2 && inFirst500 && density < 3;
    const warn = hfMatches >= 1 && density < 3;
    checks.push({ name: "ВЧ ключ", weight: 15, status: ok ? "ok" : warn ? "warn" : "fail",
      detail: `"${hfKeyword}": ${hfMatches}x, ${inFirst500 ? "в перших 500" : "не в перших 500"}, щільність ${density.toFixed(1)}%` });
  }

  // 2. MF coverage (15)
  if (!hasKeywords) checks.push({ name: "СЧ покриття", weight: 15, status: "warn", detail: "Немає семантики" });
  else if (mfKeys.length) {
    const covered = mfKeys.filter(k => phraseCovered(k.keyword));
    const pct = covered.length / mfKeys.length;
    checks.push({ name: "СЧ покриття", weight: 15, status: pct >= 0.5 ? "ok" : pct >= 0.3 ? "warn" : "fail", detail: `${covered.length}/${mfKeys.length} (${Math.round(pct * 100)}%)` });
  } else checks.push({ name: "СЧ покриття", weight: 15, status: "ok", detail: "Нема СЧ ключів" });

  // 3. LF coverage (10)
  if (!hasKeywords) checks.push({ name: "НЧ покриття", weight: 10, status: "warn", detail: "Немає семантики" });
  else if (lfKeys.length) {
    const covered = lfKeys.filter(k => phraseCovered(k.keyword));
    const pct = covered.length / lfKeys.length;
    checks.push({ name: "НЧ покриття", weight: 10, status: pct >= 0.3 ? "ok" : pct >= 0.15 ? "warn" : "fail", detail: `${covered.length}/${lfKeys.length} (${Math.round(pct * 100)}%)` });
  } else checks.push({ name: "НЧ покриття", weight: 10, status: "ok", detail: "Нема НЧ ключів" });

  // 4. N-grams (10)
  if (!hasKeywords) checks.push({ name: "N-грами", weight: 10, status: "warn", detail: "Немає семантики" });
  else if (pageNgrams.length) {
    const covered = pageNgrams.filter(g => phraseCovered(g.gram));
    const pct = covered.length / pageNgrams.length;
    checks.push({ name: "N-грами", weight: 10, status: pct >= 0.6 ? "ok" : pct >= 0.4 ? "warn" : "fail", detail: `${covered.length}/${pageNgrams.length} (${Math.round(pct * 100)}%)` });
  } else checks.push({ name: "N-грами", weight: 10, status: "ok", detail: "Нема N-грам" });

  // 5. Volume (10)
  const volOk = charCount >= recommendedLength.min && charCount <= recommendedLength.max;
  const volWarn = charCount >= recommendedLength.min * 0.7;
  checks.push({ name: "Обсяг", weight: 10, status: volOk ? "ok" : volWarn ? "warn" : "fail", detail: `${charCount} символів (рек. ${recommendedLength.min}-${recommendedLength.max})` });

  // 6. Structure (10)
  const hasH2 = /<h2/i.test(html);
  const hasH3 = /<h3/i.test(html);
  const hasParagraphs = (html.match(/<p/gi) || []).length >= 2;
  checks.push({ name: "Структура", weight: 10, status: (hasH2 && hasParagraphs) ? "ok" : (hasH2 || hasH3) ? "warn" : "fail", detail: `H2: ${hasH2 ? "+" : "-"}, H3: ${hasH3 ? "+" : "-"}, Абзаци: ${hasParagraphs ? "+" : "-"}` });

  // 7. Spam (5)
  const spamKeys = pageKeywords.filter(k => {
    const cnt = (text.match(new RegExp(k.keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi")) || []).length;
    return wordCount > 0 && (cnt / wordCount) * 100 > 3;
  });
  checks.push({ name: "Переспам", weight: 5, status: spamKeys.length === 0 ? "ok" : "fail", detail: spamKeys.length === 0 ? "Ок" : `Переспам: ${spamKeys.map(k => k.keyword).join(", ")}` });

  // ── AEO ──
  const words = text.split(/\s+/).filter(Boolean);
  const first200 = words.slice(0, 200).join(" ").toLowerCase();
  const hasKeyInFirst200 = hfKeyword ? first200.includes(hfKeyword.toLowerCase()) : true;
  checks.push({ name: "AEO: Answer Capsule", weight: 4, status: hasKeyInFirst200 ? "ok" : "fail", detail: hasKeyInFirst200 ? `"${hfKeyword}" є в перших 200 словах` : `"${hfKeyword}" відсутній в перших 200 словах` });

  const firstParagraph = text.split(/\n\n|\.\s/).filter(s => s.trim().length > 20)[0] || "";
  const startsWithFiller = /^(у цій статті|в даній|сьогодні ми|ми розповімо|давайте|у цьому|в этой статье|сегодня мы|давайте)/i.test(firstParagraph.trim());
  checks.push({ name: "AEO: Прямий початок", weight: 3, status: startsWithFiller ? "fail" : "ok", detail: startsWithFiller ? "Починається з води — AI пропустить" : "Початок без води" });

  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10);
  const avgSentLen = sentences.length ? sentences.reduce((s, sent) => s + sent.trim().split(/\s+/).length, 0) / sentences.length : 0;
  const longSentences = sentences.filter(s => s.trim().split(/\s+/).length > 25).length;
  checks.push({ name: "AEO: Довжина речень", weight: 4, status: (avgSentLen > 0 && avgSentLen <= 17) ? "ok" : avgSentLen <= 22 ? "warn" : "fail", detail: `Середня: ${avgSentLen.toFixed(1)} сл/реч (ціль <17). Довгих (>25): ${longSentences}` });

  const factMatches = text.match(/\d[\d\s,.]*(%|грн|₴|USD|\$|€|шт|л|кг|м²|мм|см|років|днів|годин|хвилин|разів|людей|клієнтів|проектів)?/g) || [];
  const factDensity = wordCount > 0 ? wordCount / Math.max(factMatches.length, 1) : 999;
  checks.push({ name: "AEO: Щільність фактів", weight: 4, status: factDensity <= 80 ? "ok" : factDensity <= 150 ? "warn" : "fail", detail: `${factMatches.length} фактів на ${wordCount} слів (1:${Math.round(factDensity)}). Ціль 1 на 80` });

  const hasFAQ = /часті\s*запитання|часто\s+задавані|часто\s+задаваемые|\bfaq\b|q\s*&\s*a|питання\s+та\s+відповіді|вопросы\s+и\s+ответы/i.test(text);
  checks.push({ name: "Без FAQ-блоку", weight: 2, status: hasFAQ ? "fail" : "ok", detail: hasFAQ ? "Знайдено FAQ-блок — приберіть" : "FAQ відсутній" });

  const hasLists = /<[uo]l[\s>]/i.test(html);
  const hasTables = /<table[\s>]/i.test(html);
  checks.push({ name: "AEO: Списки/таблиці", weight: 4, status: (hasLists || hasTables) ? "ok" : "warn", detail: `Списки: ${hasLists ? "✓" : "✗"}, Таблиці: ${hasTables ? "✓" : "✗"}` });

  const internalLinkCount = (html.match(/<a[^>]*href=["'][^"']*["']/gi) || []).length;
  checks.push({ name: "AEO: Внутрішні лінки", weight: 3, status: internalLinkCount >= 3 ? "ok" : internalLinkCount >= 1 ? "warn" : "fail", detail: `${internalLinkCount} посилань` });

  const total = checks.reduce((sum, c) => sum + (c.status === "ok" ? c.weight : c.status === "warn" ? c.weight * 0.5 : 0), 0);
  return { total: Math.round(total), checks };
}

export function scoreColor(total) {
  return total >= 70 ? "#16a34a" : total >= 40 ? "#f59e0b" : "#ef4444";
}
